import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Upload, X, Image as ImageIcon, LogOut, Trash2 } from 'lucide-react';

const AdminGallery = () => {
  const navigate = useNavigate();
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // TODO: Fetch gallery images from backend
    // For now, use mock data
    fetchGallery();
  }, [navigate]);

  const fetchGallery = async () => {
    // TODO: Replace with actual API call
    // const response = await axios.get(`${API}/admin/gallery`);
    // setGallery(response.data);
    
    // Mock data for now
    setGallery([
      {
        id: 1,
        url: "https://images.pexels.com/photos/1624695/pexels-photo-1624695.jpeg",
        title: "Port Operations",
        category: "Shipping & Logistics"
      },
      {
        id: 2,
        url: "https://images.pexels.com/photos/10881957/pexels-photo-10881957.jpeg",
        title: "Container Terminal",
        category: "Shipping & Logistics"
      },
      {
        id: 3,
        url: "https://images.pexels.com/photos/5279399/pexels-photo-5279399.jpeg",
        title: "Steel Materials",
        category: "Products"
      }
    ]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      // TODO: Implement actual file upload
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('images', file);
      });
      
      // const response = await axios.post(`${API}/admin/gallery/upload`, formData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //     'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      //   }
      // });

      alert('Images uploaded successfully!');
      setSelectedFiles([]);
      fetchGallery();
    } catch (error) {
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      // TODO: Implement actual delete
      // await axios.delete(`${API}/admin/gallery/${imageId}`, {
      //   headers: {
      //     'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      //   }
      // });

      alert('Image deleted successfully!');
      fetchGallery();
    } catch (error) {
      alert('Failed to delete image. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Gallery Management</h1>
              <p className="text-slate-600 text-sm">Feronex Global Admin Panel</p>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline">View Website</Button>
              </a>
              <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Upload Section */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Upload New Images</h2>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-slate-700 font-medium">Click to upload</span>
                  <span className="text-slate-500"> or drag and drop</span>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-slate-500 mt-2">PNG, JPG, JPEG up to 10MB each</p>
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="font-medium text-slate-700">Selected Files ({selectedFiles.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="bg-slate-100 px-3 py-2 rounded-lg text-sm text-slate-700 flex items-center gap-2">
                        {file.name}
                        <button
                          onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
                          className="text-slate-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || uploading}
                className="w-full bg-slate-700 hover:bg-slate-800"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Image(s)`}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Gallery Grid */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Gallery Images ({gallery.length})</h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {gallery.map((image) => (
              <Card key={image.id} className="overflow-hidden group">
                <div className="relative">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      onClick={() => handleDelete(image.id)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="font-medium text-slate-900 text-sm truncate">{image.title}</p>
                  <p className="text-xs text-slate-500">{image.category}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {gallery.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No images in gallery yet. Upload your first image!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
